import CONFIG from 'Config/settings';
import * as stringUtils from 'Utils/string/stringUtils';
import { getIRWResidenceType } from './transformers';

function isIntegrityValueChanged(oldValue, newValue) {
  return oldValue !== newValue;
}

export function updateProfileRequest(currentProfile, updatedProfile) {
  const transformed = {
    crmCustomerProfile: {
      noOfChildren: updatedProfile.noOfChildren,
      maritalStatusCode: updatedProfile.maritalStatus,
      privateMaintCustomer: {
        namePrefix: updatedProfile.title,
        firstName: updatedProfile.firstName,
        lastName: updatedProfile.lastName,
        firstNamePhonetic: updatedProfile.firstNamePhonetic,
        lastNamePhonetic: updatedProfile.lastNamePhonetic,
        secondLastName: updatedProfile.lastName2,
        genderCode: updatedProfile.genderCode,
        languageCode: updatedProfile.preferredLanguage,
        countryCode: CONFIG.COUNTRY.toUpperCase(),
        birthDate:
          /^SE$/i.test(CONFIG.COUNTRY) && updatedProfile.socialSecurityNumber
            ? undefined
            : updatedProfile.birthDate
            ? stringUtils.reverseString(updatedProfile.birthDate, '-')
            : undefined,
        contactMethodList: buildContactMethods(updatedProfile)
      },
      // The below check is required to send the customer integrity values only when it is updated.
      // Sending it without real update is considered as an update from the IRW side which is against the consents update requirements.
      ...(isCustomerIntegrityChanged(currentProfile, updatedProfile) && {
        crmCustomerIntegrity: {
          ...(isIntegrityValueChanged(
            currentProfile.allowEmail,
            updatedProfile.allowEmail
          ) && {
            notAllowPersonalEmailCode:
              updatedProfile.allowEmail === true
                ? 0
                : updatedProfile.allowEmail === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.allowSMS,
            updatedProfile.allowSMS
          ) && {
            notAllowSms:
              updatedProfile.allowSMS === true
                ? 0
                : updatedProfile.allowSMS === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.allowSharingToSocialMedia,
            updatedProfile.allowSharingToSocialMedia
          ) && {
            notAllowSharingToSocialMedia:
              updatedProfile.allowSharingToSocialMedia === true
                ? 0
                : updatedProfile.allowSharingToSocialMedia === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.allowFindPotentialCustomers,
            updatedProfile.allowFindPotentialCustomers
          ) && {
            notAllowFindPotentialCustomers:
              updatedProfile.allowFindPotentialCustomers === true
                ? 0
                : updatedProfile.allowFindPotentialCustomers === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.allowAppPushNotification,
            updatedProfile.allowAppPushNotification
          ) && {
            notAllowAppPushNotification:
              updatedProfile.allowAppPushNotification === true
                ? 0
                : updatedProfile.allowAppPushNotification === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.allowWhatsApp,
            updatedProfile.allowWhatsApp
          ) && {
            notAllowWhatsApp:
              updatedProfile.allowWhatsApp === true
                ? 0
                : updatedProfile.allowWhatsApp === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.allowDirectMail,
            updatedProfile.allowDirectMail
          ) && {
            notAllowDirectMailCode:
              updatedProfile.allowDirectMail === true
                ? 0
                : updatedProfile.allowDirectMail === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.allowCollectBuyInfo,
            updatedProfile.allowCollectBuyInfo
          ) && {
            notAllowCollectBuyInfoCode:
              updatedProfile.allowCollectBuyInfo === true
                ? 0
                : updatedProfile.allowCollectBuyInfo === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.allowPhone,
            updatedProfile.allowPhone
          ) && {
            notAllowPhoneContactCode:
              updatedProfile.allowPhone === true
                ? 0
                : updatedProfile.allowPhone === false
                ? 1
                : undefined
          }),
          ...(isIntegrityValueChanged(
            currentProfile.sendFamilyPlasticCard,
            updatedProfile.sendFamilyPlasticCard
          ) && {
            OptOutPlasticFamilyCard:
              updatedProfile.sendFamilyPlasticCard === true
                ? 0
                : updatedProfile.sendFamilyPlasticCard === false
                ? 1
                : undefined
          })
        }
      }),
      crmInterestAreaGroupList: [
        {
          interestAreaGroupType: 'InterestCodes',
          crmInterestAreaList: buildInterests(
            updatedProfile.interestCodes,
            updatedProfile.participateInMemberPanel,
            updatedProfile.subscribeToIKEACatalogue
          )
        }
      ],
      CrmCustomerChildList: buildChildList(updatedProfile),
      preferredBuCode: updatedProfile.preferredStore,
      preferredBuType: 'STO',
      socialSecurityNo: updatedProfile.socialSecurityNumber,
      residenceInfo: {
        id: updatedProfile.residenceId,
        noOfRooms: updatedProfile.noOfRooms,
        coExistenceInfo: updatedProfile.coExistenceInfo,
        homeOwnershipIndicator: updatedProfile.homeOwnership,
        residenceType: getIRWResidenceType(
          updatedProfile.residenceType,
          updatedProfile.outdoorType
        ),
        holidayHomeOwnership: updatedProfile.holidayHomeOwnership,
        livingSpaceUnit: updatedProfile.livingSpace
      }
    },
    profileType: 'MINIMUM',
    agreeToIKEAPolicy: updatedProfile.policyAccept
  };
  if (updatedProfile.challengeCode) {
    transformed.challengeContextList = {
      challengeContext: {
        challengeId: updatedProfile.challengeId,
        secretToken: updatedProfile.challengeToken,
        challengeCode: updatedProfile.challengeCode
      }
    };
  }
  return transformed;
}

function buildContactMethods(updatedProfile) {
  const contactMethods = [];
  const extractHouseNumber = CONFIG.FEATURE.EXTRACT_HOUSE_NUMBER;
  contactMethods.push({
    contactMethodType: 'MOBILE',
    contactMethodData: updatedProfile.mobile
  });
  contactMethods.push({
    contactMethodType: 'EMAIL',
    contactMethodData: updatedProfile.email
  });
  contactMethods.push({
    contactMethodType: 'PHONE',
    contactMethodData: updatedProfile.landline
  });
  if (updatedProfile.address1 || updatedProfile.zipCode) {
    contactMethods.push({
      contactMethodType: 'ADDRESS',
      contactMethodRoleList: [{ contactMethodRoleId: 'INVOICE_DEST' }],
      address: {
        cityName: updatedProfile.cityName,
        zipCode: updatedProfile.zipCode,
        provinceCode: updatedProfile.province,
        countryCode:
          updatedProfile.addressCountryCode || CONFIG.COUNTRY.toUpperCase(),
        addressId: updatedProfile.addressId,
        formattedAddress: {
          address1: extractHouseNumber
            ? updatedProfile.address1
                .trim()
                .concat(` ${updatedProfile.houseNumber}`)
            : updatedProfile.address1,
          address2: updatedProfile.address2,
          address3: updatedProfile.address3
        }
      }
    });
  }
  updatedProfile.deliveryAddresses?.forEach(deliveryAddress => {
    contactMethods.push({
      contactMethodType: 'ADDRESS',
      contactMethodRoleList: [{ contactMethodRoleId: 'DELIVERY_DEST' }],
      address: {
        cityName: deliveryAddress.cityName,
        zipCode: deliveryAddress.zipCode,
        provinceCode: deliveryAddress.province,
        countryCode:
          deliveryAddress.addressCountryCode || CONFIG.COUNTRY.toUpperCase(),
        addressId: deliveryAddress.addressId,
        firstName: deliveryAddress.firstName,
        lastName: deliveryAddress.lastName,
        firstNamePhonetic: deliveryAddress.firstNamePhonetic,
        lastNamePhonetic: deliveryAddress.lastNamePhonetic,
        mobile: deliveryAddress.mobile,
        phone: deliveryAddress.landline,
        formattedAddress: {
          address1: extractHouseNumber
            ? deliveryAddress.address1
                .trim()
                .concat(` ${deliveryAddress.houseNumber}`)
            : deliveryAddress.address1,
          address2: deliveryAddress.address2,
          address3: deliveryAddress.address3
        }
      }
    });
  });
  return contactMethods;
}

function buildInterests(
  interestCodes,
  participateInMemberPanel,
  subscribeToIKEACatalogue
) {
  const interestList = [];
  if (participateInMemberPanel) {
    interestList.push({ interestAreaCode: '60' });
  }
  if (subscribeToIKEACatalogue) {
    interestList.push({ interestAreaCode: '90' });
  }
  if (interestCodes) {
    interestCodes.forEach(interestCode => {
      interestList.push({
        interestAreaCode: interestCode
      });
    });
  }
  return interestList;
}

function buildChildList(data) {
  const childFieldNames = Object.keys(data).filter(key =>
    key.startsWith('child-')
  );

  const newChildList = childFieldNames.reduce((childList, fieldName) => {
    if (!data[fieldName]) {
      return childList;
    }
    const [_, childProperty, childNumber] = fieldName.split('-');
    childList[childNumber] = childList[childNumber] || {};
    childList[childNumber][childProperty] = data[fieldName];

    return childList;
  }, []);

  // Remove child objects that only has id.
  const filteredFromEmptyChildren = newChildList.filter(child => {
    const childKeys = Object.keys(child);
    return 'birthDate' in child || childKeys[0] !== 'id';
  });

  return filteredFromEmptyChildren;
}

function isCustomerIntegrityChanged(currentProfile, updatedProfile) {
  if (
    currentProfile.allowEmail !== updatedProfile.allowEmail ||
    currentProfile.allowSMS !== updatedProfile.allowSMS ||
    currentProfile.allowSharingToSocialMedia !==
      updatedProfile.allowSharingToSocialMedia ||
    currentProfile.allowFindPotentialCustomers !==
      updatedProfile.allowFindPotentialCustomers ||
    currentProfile.allowAppPushNotification !==
      updatedProfile.allowAppPushNotification ||
    currentProfile.allowCollectBuyInfo !== updatedProfile.allowCollectBuyInfo ||
    currentProfile.allowDirectMail !== updatedProfile.allowDirectMail ||
    currentProfile.allowPhone !== updatedProfile.allowPhone ||
    currentProfile.sendFamilyPlasticCard !==
      updatedProfile.sendFamilyPlasticCard
  ) {
    return true;
  }
  return false;
}
